import { useCallback, useMemo, useRef, useState } from 'react';
import type { GridView } from './gridView';
import { defaultView, reconcileView } from './gridView';

function storageKey(key: string): string {
  return `oip.grid.${key}`;
}

/** Column/density view state — controlled (view+onViewChange) or self-persisting (persistKey). */
export function useGridView(
  keys: string[],
  opts: { persistKey?: string; view?: GridView; onViewChange?: (v: GridView) => void },
): [GridView, (updater: GridView | ((prev: GridView) => GridView)) => void] {
  const initial = useMemo(() => {
    if (opts.persistKey) {
      try {
        const raw = localStorage.getItem(storageKey(opts.persistKey));
        return reconcileView(raw ? (JSON.parse(raw) as GridView) : null, keys);
      } catch {
        return defaultView(keys);
      }
    }
    return defaultView(keys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.persistKey]);

  const [internal, setInternal] = useState<GridView>(initial);
  const persistKeyRef = useRef(opts.persistKey);
  persistKeyRef.current = opts.persistKey;

  const controlled = opts.view;
  const view = controlled ?? internal;

  const setView = useCallback(
    (updater: GridView | ((prev: GridView) => GridView)) => {
      const compute = (prev: GridView) => (typeof updater === 'function' ? updater(prev) : updater);
      if (opts.onViewChange) {
        opts.onViewChange(compute(controlled ?? internal));
        return;
      }
      setInternal((prev) => {
        const next = compute(prev);
        if (persistKeyRef.current) {
          try {
            localStorage.setItem(storageKey(persistKeyRef.current), JSON.stringify(next));
          } catch {
            /* ignore */
          }
        }
        return next;
      });
    },
    [opts, controlled, internal],
  );

  return [view, setView];
}
